import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createRouter, Navigate, RouterProvider } from '@tanstack/react-router'
import { StrictMode, Suspense } from 'react'
import { createRoot, type Root } from 'react-dom/client'
import './index.css'

import type { PublicClientApplication } from '@azure/msal-browser'
import { useIsAuthenticated } from '@azure/msal-react'
import { ErrorBoundary } from '@components/ErrorBoundary.tsx'
import { MSWControl } from '@components/MSWControl/MSWControl'
import { NotFound } from '@features/NotFound'
import { DialogProvider } from '@hooks/useDialog'
import { ApiError } from '@services/ApiClient'
// Import the generated route tree
import { loadRuntimeConfig } from '@/config/runtime-config'
import type { GetForestOwnersParams } from '@/types/ForestOwners/ForestOwner'
import { routeTree } from './routeTree.gen'

/**
 * Determines if an error should be retried.
 * Client errors (4xx) should not be retried, except for 408 (Request Timeout).
 * Server errors (5xx) and network errors should be retried.
 */
const isRetryableError = (error: unknown): boolean => {
  if (error instanceof ApiError) {
    const status = error.status
    // Don't retry on 4xx client errors (400, 401, 403, 404, etc.)
    // Exception: 408 (Request Timeout) can be retried as it may be transient
    if (status >= 400 && status < 500 && status !== 408) {
      return false
    }
    // Retry 5xx server errors and other errors
    return true
  }

  // For non-ApiError errors (network errors, etc.), retry them
  return true
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry(failureCount, error) {
        if (!isRetryableError(error)) {
          return false
        }
        // Retry up to 3 times for retryable errors
        return failureCount < 3
      },
    },
    mutations: {
      retry(failureCount, error) {
        if (!isRetryableError(error)) {
          return false
        }
        // Retry up to 3 times for retryable errors
        return failureCount < 3
      },
    },
  },
})

// Create router synchronously to maintain type inference
// We use undefined! as placeholder for auth since it will be set before any routes render
export const router = createRouter({
  routeTree,
  defaultNotFoundComponent: NotFound,
  context: { queryClient, auth: undefined as unknown as PublicClientApplication },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
  defaultViewTransition: true,
})

export type MyRouter = typeof router

export const protectedRouteGuard = () => {
  const isAuthenticated = useIsAuthenticated()
  // Redirect to the home page if unauthenticated
  return isAuthenticated ? true : () => <Navigate to="/" />
}

// Dashboard the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: MyRouter
  }
  interface HistoryState {
    fromSearch?: GetForestOwnersParams | string | boolean
    fromSalesAgreementDetail?: boolean
    isEstateSettlement?: boolean
    justCreated?: boolean
    editSuccess?: boolean
  }
  interface StaticDataRouteOption {
    breadcrumb?: string
  }
}

// Global type-safe storage for root instance
declare global {
  interface Window {
    _root?: Root
  }
}

const container = document.getElementById('root')

if (!container) {
  throw new Error("Root container element with id 'root' not found.")
}

window._root ??= createRoot(container)

const renderApp = ({
  MsalLoader,
}: {
  MsalLoader: React.ComponentType<{ children: React.ReactNode }>
}) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  window._root?.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary fallback={<div>Something went wrong...</div>}>
          <Suspense fallback={<div>Loading...</div>}>
            <MsalLoader>
              <DialogProvider>
                <RouterProvider router={router} />
                <MSWControl />
              </DialogProvider>
            </MsalLoader>
          </Suspense>
        </ErrorBoundary>
      </QueryClientProvider>
    </StrictMode>,
  )
}

loadRuntimeConfig()
  .then(async (config) => {
    // In production, config.json is required
    if (import.meta.env.PROD && !config) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      window._root?.render(
        <div style={{ padding: '2rem', textAlign: 'center', fontFamily: 'system-ui' }}>
          <h1 style={{ color: '#dc2626' }}>Configuration Error</h1>
          <p>Application configuration is missing. Please contact support.</p>
          <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '1rem' }}>
            Error: config.json not found
          </p>
        </div>,
      )
      return
    }

    // In development, warn but continue with .env fallback
    if (import.meta.env.DEV && !config) {
      console.warn('Runtime config not found, using .env variables for local development')
    }

    // MSW is now initialized conditionally via useMSW hook when scenario is present in URL

    // NOW initialize MSAL after config is loaded
    const msalModule = await import('@utils/msal.ts')
    const msalInstance = msalModule.msalInstance

    // Import MsalLoader dynamically (it depends on msalInstance)
    const msalLoaderModule = await import('@utils/MsalLoader.tsx')
    const MsalLoader = msalLoaderModule.MsalLoader

    // Update router context with initialized MSAL instance
    router.update({
      context: { queryClient, auth: msalInstance },
    })

    renderApp({ MsalLoader })
  })
  .catch((error: unknown) => {
    // This should never happen with the updated loadRuntimeConfig
    // but keeping as a safety net
    console.error('Unexpected error during initialization:', error)
  })
